/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office */

import { fetchBranches, fetchCountryCode, postContract, postDocuments } from "../helpers/sso-helper";

var runCounter = 0;
let branchesLoaded = false;
let activeTab: "movdoc" | "contracts" = "movdoc";

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    setupTabs();
    setupMovdocControls();
    setupContractsControls();

    fetchCountryCode(callbackMethod);

    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, itemChanged);
    updateTaskPaneUI(Office.context.mailbox.item);
  }
});

function setupTabs() {
  const movdocTabButton = document.getElementById("movdocTabButton") as HTMLButtonElement;
  const contractsTabButton = document.getElementById("contractsTabButton") as HTMLButtonElement;
  const movdocPanel = document.getElementById("movdoc-panel");
  const contractsPanel = document.getElementById("contracts-panel");

  const setActiveTab = async (tab: "movdoc" | "contracts") => {
    activeTab = tab;
    const isMovdoc = tab === "movdoc";
    movdocTabButton.classList.toggle("active", isMovdoc);
    contractsTabButton.classList.toggle("active", !isMovdoc);
    movdocPanel.classList.toggle("active", isMovdoc);
    contractsPanel.classList.toggle("active", !isMovdoc);

    if (!isMovdoc && !branchesLoaded) {
      await fetchBranches(branchesCallback);
      branchesLoaded = true;
    }
  };

  movdocTabButton.onclick = () => setActiveTab("movdoc");
  contractsTabButton.onclick = () => setActiveTab("contracts");
  setActiveTab("movdoc");
}

function setupMovdocControls() {
  const button = document.getElementById("archiveButton") as HTMLButtonElement;
  if (button) {
    button.disabled = true;
    button.onclick = () => {
      button.disabled = true;
      run(documentsCallback);
    };
  }
}

function setupContractsControls() {
  const button = document.getElementById("contractUploadButton") as HTMLButtonElement;
  const branchSelect = document.getElementById("contractsBranchSelect") as HTMLSelectElement;
  const companyTypeSelect = document.getElementById("contractsCompanyTypeSelect") as HTMLSelectElement;
  if (button) {
    button.disabled = true;
    button.onclick = () => {
      button.disabled = true;
      runContract(contractCallback);
    };
  }

  if (branchSelect) {
    branchSelect.onchange = () => {
      if (companyTypeSelect) {
        companyTypeSelect.disabled = !branchSelect.value;
        if (!branchSelect.value) {
          companyTypeSelect.value = "";
        }
      }
      updateContractUploadButtonState();
    };
  }

  if (companyTypeSelect) {
    companyTypeSelect.onchange = () => {
      updateContractUploadButtonState();
    };
  }
}

function callbackMethod(countries) {
  const select = document.getElementById("gdc") as HTMLSelectElement;
  select.innerHTML = "";

  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.text = "Select a country";
  placeholder.disabled = true;
  placeholder.selected = true;
  select.appendChild(placeholder);

  for (let i = 0; i < countries.length; i++) {
    const option = document.createElement("option");
    option.value = countries[i].key;
    option.text = countries[i].key;
    select.appendChild(option);
  }

  const button = document.getElementById("archiveButton") as HTMLButtonElement;
  select.onchange = () => {
    if (!button) return;
    button.disabled = select.value === "";
  };

  if (button) {
    button.disabled = select.value === "";
  }
}

function branchesCallback(branches) {
  const select = document.getElementById("contractsBranchSelect") as HTMLSelectElement;
  if (!select) {
    return;
  }

  select.innerHTML = "";
  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.text = "Select a branch";
  placeholder.disabled = true;
  placeholder.selected = true;
  select.appendChild(placeholder);

  for (let i = 0; i < branches.length; i++) {
    const option = document.createElement("option");
    option.value = String(branches[i].value);
    option.text = branches[i].label;
    select.appendChild(option);
  }
}

const maxAttachmentNameLength = 15;

function createAttachmentLabel(forId: string, name: string): HTMLLabelElement {
  const label = document.createElement("label");
  label.htmlFor = forId;
  label.className = "attachment-label";
  label.title = name;

  const strong = document.createElement("strong");
  strong.textContent =
    name.length > maxAttachmentNameLength ? `${name.substring(0, maxAttachmentNameLength)}...` : name;
  label.appendChild(strong);
  return label;
}

function createDocumentList() {
  const attachments = Office.context.mailbox.item.attachments || [];
  let attachmentData = document.getElementById("attachmentdata");
  if (!attachmentData) {
    attachmentData = document.createElement("div");
    attachmentData.id = "attachmentData";
    attachmentData.className = "attachment-data";
    document.body.appendChild(attachmentData);
  } else {
    attachmentData.innerHTML = "";
  }

  for (let i = 0; i < attachments.length; i++) {
    const attachment = attachments[i];
    const attachmentDiv = document.createElement("div");
    attachmentDiv.id = "attachment" + i;
    attachmentDiv.className = "attachment-item";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.id = attachment.id;
    checkbox.checked = attachment.size > 12000;
    checkbox.className = "attachment-checkbox";

    attachmentDiv.appendChild(checkbox);
    attachmentDiv.appendChild(createAttachmentLabel(checkbox.id, attachment.name));
    attachmentData.appendChild(attachmentDiv);
  }
}

function createContractDocumentList() {
  const attachments = Office.context.mailbox.item.attachments || [];
  const attachmentData = document.getElementById("contractsAttachmentData");
  if (!attachmentData) {
    return;
  }
  attachmentData.innerHTML = "";

  for (let i = 0; i < attachments.length; i++) {
    const attachment = attachments[i];
    const attachmentDiv = document.createElement("div");
    attachmentDiv.id = "contractAttachment" + i;
    attachmentDiv.className = "attachment-item";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.name = "contractAttachment";
    checkbox.id = `contract-${attachment.id}`;
    checkbox.value = attachment.id;
    checkbox.checked = attachment.size > 12000;
    checkbox.className = "attachment-checkbox";
    checkbox.onchange = () => updateContractUploadButtonState();

    attachmentDiv.appendChild(checkbox);
    attachmentDiv.appendChild(createAttachmentLabel(checkbox.id, attachment.name));
    attachmentData.appendChild(attachmentDiv);
  }
}

function getSelectedContractAttachmentIds(): string[] {
  const checked = document.querySelectorAll("input[name='contractAttachment']:checked");
  return Array.from(checked).map((x) => (x as HTMLInputElement).value);
}

function updateContractUploadButtonState() {
  const branchSelect = document.getElementById("contractsBranchSelect") as HTMLSelectElement;
  const companyTypeSelect = document.getElementById("contractsCompanyTypeSelect") as HTMLSelectElement;
  const contractButton = document.getElementById("contractUploadButton") as HTMLButtonElement;
  if (!contractButton) {
    return;
  }

  contractButton.disabled = !(
    branchSelect?.value &&
    companyTypeSelect?.value &&
    getSelectedContractAttachmentIds().length > 0
  );
}

function documentsCallback(docs) {
  const documentJson = typeof docs === "string" ? JSON.parse(docs) : docs;
  if (
    documentJson &&
    documentJson.documents &&
    documentJson.documents.uploadedDocuments &&
    documentJson.documents.uploadedDocuments.length > 0
  ) {
    const documentIds = documentJson.documents.uploadedDocuments.map((doc) => doc.documentId).join("-");

    const selectedCountryCode = documentJson.GDC.toLowerCase();
    const url = `${resolvePortalOrigin()}/${selectedCountryCode}/add-ins/outlook/${documentIds}`;
    setMessageAndOpenUrl(url, "Click this link if you need to go back to Gemisoft to Archive.");
  } else {
    runCounter++;
    if (runCounter < 3) {
      run(documentsCallback);
    }
  }
}

const companyTypePathMap: Record<string, string> = {
  producer: "add-producer-contract",
  "recovery-facility": "add-recovery-facility-contract",
  transporter: "add-transporter-contract",
  other: "add-other-company-contract",
};

function contractCallback(response) {
  let result = typeof response === "string" ? JSON.parse(response) : response;
  if (typeof result === "string") {
    result = JSON.parse(result);
  }

  const documents: any[] = result?.documents ?? result?.Documents ?? [];
  if (documents.length === 0) {
    setErrorMessage(result?.message || result?.Message || "Contract document upload failed.");
    updateContractUploadButtonState();
    return;
  }

  const companyTypeSelect = document.getElementById("contractsCompanyTypeSelect") as HTMLSelectElement;
  const companyTypePath = companyTypePathMap[companyTypeSelect?.value];
  if (!companyTypePath) {
    throw new Error("Invalid company type selected.");
  }

  const branchId =
    result.branchId ??
    result.BranchId ??
    (document.getElementById("contractsBranchSelect") as HTMLSelectElement)?.value;
  const query = new URLSearchParams({ branchId: String(branchId) });
  documents.forEach((doc) => {
    query.append("documentId", String(doc.documentId ?? doc.DocumentId));
    query.append("documentName", doc.documentName || doc.DocumentName || "Contract document");
  });
  const url = `${resolvePortalOrigin()}/geminor/contracts/${companyTypePath}?${query.toString()}`;
  const message =
    documents.length === 1
      ? "Document uploaded. Open the contract form in Gemisoft:"
      : `${documents.length} documents uploaded. Open the contract form in Gemisoft:`;
  setMessageAndOpenUrl(url, message);
}

function resolvePortalHost(): string {
  const host = location.host.toLowerCase();
  if (host.includes("localhost") || host.includes("127.0.0.1")) return "localhost:3000";
  if (host.includes("gemisoft.net")) return "gemisoft.net";
  return "gemisoft.dev";
}

function resolvePortalOrigin(): string {
  const portalHost = resolvePortalHost();
  const protocol = portalHost.startsWith("localhost") ? "http" : "https";
  return `${protocol}://${portalHost}`;
}

function setMessageAndOpenUrl(url: string, message: string) {
  const messageArea = document.getElementById("message-area");
  if (messageArea) {
    messageArea.innerHTML = `<p>${message}<br><br>Link will open in a new tab.</p><a href="${url}" target="_blank">${url}</a>`;
  }
  window.open(url, "_blank");
}

function setErrorMessage(message: string) {
  const messageArea = document.getElementById("message-area");
  if (messageArea) {
    messageArea.innerHTML = `<p>${message}</p>`;
  }
}

function attachmentToBlob(attachment: Office.AttachmentDetails, content: string): Blob {
  const binaryString = atob(content);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const isPdf = attachment.name.toLowerCase().endsWith(".pdf");
  return new Blob([bytes], { type: isPdf ? "application/pdf" : "application/octet-stream" });
}

function getAttachmentAsBlob(attachment: Office.AttachmentDetails): Promise<Blob> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAttachmentContentAsync(attachment.id, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(attachmentToBlob(attachment, result.value.content));
      } else {
        reject(result.error);
      }
    });
  });
}

export async function run(callback) {
  const body = Office.context.mailbox.item.body;
  const attachmentPromises = [];
  const documents = new FormData();
  body.getAsync(Office.CoercionType.Text, async function () {
    const attachments = Office.context.mailbox.item.attachments || [];

    if (attachments.length > 0) {
      attachments.forEach((attachment) => {
        const checkbox = document.getElementById(attachment.id) as HTMLInputElement;
        if (!checkbox || !checkbox.checked) {
          return;
        }

        const promise = getAttachmentAsBlob(attachment).then((blob) => {
          documents.append("file", blob, attachment.name);
        });
        attachmentPromises.push(promise);
      });
    } else {
      runCounter++;
      if (runCounter < 3) {
        run(callback);
      }
      return;
    }

    await Promise.all(attachmentPromises);
    const selectedCountryCode = (document.getElementById("gdc") as HTMLSelectElement).value;
    postDocuments(documents, selectedCountryCode, callback);
  });
}

async function runContract(callback) {
  const branchSelect = document.getElementById("contractsBranchSelect") as HTMLSelectElement;
  const companyTypeSelect = document.getElementById("contractsCompanyTypeSelect") as HTMLSelectElement;
  const selectedIds = getSelectedContractAttachmentIds();

  if (!branchSelect?.value || !companyTypeSelect?.value || selectedIds.length === 0) {
    updateContractUploadButtonState();
    return;
  }

  const attachments = Office.context.mailbox.item.attachments || [];
  const selectedAttachments = attachments.filter((x) => selectedIds.includes(x.id));
  if (selectedAttachments.length === 0) {
    setErrorMessage("Could not find selected attachments.");
    updateContractUploadButtonState();
    return;
  }

  try {
    const blobs = await Promise.all(selectedAttachments.map((attachment) => getAttachmentAsBlob(attachment)));
    const contractDocuments = new FormData();
    blobs.forEach((blob, i) => contractDocuments.append("file", blob, selectedAttachments[i].name));
    contractDocuments.append("branchId", branchSelect.value);
    await postContract(contractDocuments, callback);
  } catch (error: any) {
    setErrorMessage(error?.message || "Failed to upload selected contracts.");
    updateContractUploadButtonState();
  }
}

function itemChanged() {
  updateTaskPaneUI(Office.context.mailbox.item);

  const archiveButton = document.getElementById("archiveButton") as HTMLButtonElement;
  if (archiveButton) {
    archiveButton.disabled = true;
  }

  const contractButton = document.getElementById("contractUploadButton") as HTMLButtonElement;
  if (contractButton) {
    contractButton.disabled = true;
  }

  const messageArea = document.getElementById("message-area");
  if (messageArea) {
    messageArea.innerHTML = "";
  }

  const countrySelect = document.getElementById("gdc") as HTMLSelectElement;
  if (countrySelect) {
    countrySelect.value = "";
  }

  const branchSelect = document.getElementById("contractsBranchSelect") as HTMLSelectElement;
  if (branchSelect) {
    branchSelect.value = "";
  }

  const companyTypeSelect = document.getElementById("contractsCompanyTypeSelect") as HTMLSelectElement;
  if (companyTypeSelect) {
    companyTypeSelect.value = "";
    companyTypeSelect.disabled = true;
  }
}

function updateTaskPaneUI(item) {
  if (item != null) {
    createDocumentList();
    createContractDocumentList();
    if (activeTab === "contracts") {
      updateContractUploadButtonState();
    }
  }
}
